import type { ToastServiceMethods } from "primevue/toastservice";

type AnyError =
  | { code?: string | number; message?: string; status?: number; name?: string }
  | Error
  | unknown;

// Postgres / PostgREST error codes → friendly i18n keys.
// Anything not mapped falls back to the raw error.message (still useful for
// debugging unusual cases) wrapped under the generic "Something went wrong" summary.
const FRIENDLY_CODE_MAP: Record<string, string> = {
  PGRST116: "error_not_found",
  "23505": "error_already_exists",
  "23503": "error_in_use_elsewhere",
  "23502": "error_required_field",
  "23514": "error_invalid_value",
  "42501": "error_no_permission",
  "40001": "error_conflict_retry",
  // organization with blocked content (organization_is_blocked): free tickets paused
  TA001: "error_organization_blocked",
};

export function isSupabaseJwtError(error: AnyError): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as {
    code?: string | number;
    message?: string;
    status?: number;
    name?: string;
  };
  if (e.code === "PGRST301" || e.code === 401 || e.status === 401) return true;
  if (typeof e.message === "string") {
    if (/jwt\s+(expired|invalid|malformed)/i.test(e.message)) return true;
    if (/invalid\s+(jwt|token)/i.test(e.message)) return true;
    if (/refresh\s+token\s+(not\s+found|expired)/i.test(e.message)) return true;
  }
  return false;
}

function isNetworkError(error: AnyError): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as { name?: string; message?: string };
  const msg = e.message ?? "";
  if (e.name === "TypeError" && /fetch|network/i.test(msg)) return true;
  if (/failed to fetch|network\s*error|networkerror/i.test(msg)) return true;
  return false;
}

/** The friendly explanation for a known error (network, permission, ...), or null when there is none. */
export function knownErrorText(error: AnyError, t: (key: string) => string): string | null {
  const e = (error ?? {}) as { code?: string | number };
  const code = e.code != null ? String(e.code) : undefined;
  // Inline errors say it in words; the toast helper stays silent here because the session guard takes over
  if (isSupabaseJwtError(error)) return t("session_expired_detail");
  if (isNetworkError(error)) return t("error_network");
  if (code && FRIENDLY_CODE_MAP[code]) return t(FRIENDLY_CODE_MAP[code]);
  return null;
}

/** What to tell the organizer about an error: the friendly explanation, else its own message, else "something went wrong". */
export function friendlyErrorText(error: AnyError, t: (key: string) => string): string {
  return knownErrorText(error, t) || (error as { message?: string } | null)?.message || t("error_unknown_detail");
}

export function showSupabaseError(
  toast: ToastServiceMethods,
  error: AnyError,
  options?: { summary?: string; detail?: string; life?: number },
): boolean {
  if (isSupabaseJwtError(error)) return false;

  const e = (error ?? {}) as {
    code?: string | number;
    message?: string;
    name?: string;
  };
  const code = e.code != null ? String(e.code) : undefined;

  // Always log technical info — UI message is friendly, console keeps the details.
  console.error("[supabase]", { code, message: e.message, error });

  let t: (key: string) => string;
  try {
    const i18n = useNuxtApp().$i18n as { t?: (key: string) => string } | undefined;
    t = i18n?.t?.bind(i18n) ?? ((k: string) => k);
  } catch {
    t = (k: string) => k;
  }

  const summary = options?.summary ?? t("error_generic_summary");
  let detail = options?.detail;

  if (!detail) detail = friendlyErrorText(error, t);

  toast.add({
    severity: "error",
    summary,
    detail,
    life: options?.life ?? 8000,
  });
  return true;
}
